/**
 * Generic styled XLSX export for tabular reports — built with exceljs (lazy-loaded).
 * Mirrors the design language of export-cari-movements-xlsx.ts:
 * navy header FF1E3A5F, zebra FFFAFBFC, letterhead block, params strip,
 * real numeric cells with '#,##0.00' format.
 *
 * Driven entirely by ReportTableDoc so any columnar report can reuse this.
 */
import type { ReportTableDoc, ReportColumn } from './report-table-types';

const NAVY      = 'FF1E3A5F';
const NAVY_SOFT = 'FFEEF2F7';
const NAVY_GROUP = 'FFD6E0ED';
const INK       = 'FF111827';
const INK_SOFT  = 'FF4B5563';
const WHITE     = 'FFFFFFFF';
const LINE      = 'FFE5E7EB';
const ZEBRA     = 'FFFAFBFC';

const NUM_FMT = '#,##0.00';

function colLetter(n: number): string {
  let str = '';
  while (n > 0) {
    const m = (n - 1) % 26;
    str = String.fromCharCode(65 + m) + str;
    n = Math.floor((n - 1) / 26);
  }
  return str;
}

function sanitizeSheetName(title: string): string {
  return (title.replace(/[\\/?*[\]:]/g, ' ').slice(0, 28) || 'Rapor');
}

function colAlignment(col: ReportColumn): 'left' | 'right' | 'center' {
  if (col.num) return 'right';
  return col.align ?? 'left';
}

/** Build the styled workbook. Exported for reuse (e.g., Node tests). */
export async function buildTableReportWorkbook(doc: ReportTableDoc): Promise<import('exceljs').Workbook> {
  const mod = (await import('exceljs')) as typeof import('exceljs') & { default?: typeof import('exceljs') };
  const ExcelJS = mod.default ?? mod;
  const workbook = new ExcelJS.Workbook();
  workbook.creator = doc.company.name;
  workbook.created = new Date();

  const { company, title, params, generatedLabel, columns, rows } = doc;
  const numCols = columns.length;
  const lastCol = colLetter(numCols);

  const ws = workbook.addWorksheet(sanitizeSheetName(title), {
    pageSetup: {
      paperSize: 9,
      orientation: 'landscape',
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      margins: { left: 0.3, right: 0.3, top: 0.4, bottom: 0.4, header: 0.2, footer: 0.2 },
    },
  });

  ws.columns = columns.map((c) => ({ width: c.num ? (c.width ?? 12) : (c.width ?? 16) }));

  const thin = { style: 'thin' as const, color: { argb: LINE } };

  // ── Letterhead ────────────────────────────────────────────────────────────
  ws.mergeCells(`A1:${lastCol}1`);
  const nameCell = ws.getCell('A1');
  nameCell.value = company.name;
  nameCell.font = { name: 'Calibri', size: 16, bold: true, color: { argb: NAVY } };
  ws.getRow(1).height = 22;

  const addressParts: string[] = [];
  if (company.address) addressParts.push(company.address);
  if (company.phone)   addressParts.push(company.phone);
  if (company.email)   addressParts.push(company.email);
  if (company.web)     addressParts.push(company.web);

  ws.mergeCells(`A2:${lastCol}2`);
  ws.getCell('A2').value = addressParts.join('  ·  ');
  ws.getCell('A2').font = { name: 'Calibri', size: 9, color: { argb: INK_SOFT } };

  // ── Title + params ────────────────────────────────────────────────────────
  ws.mergeCells(`A3:${lastCol}3`);
  ws.getCell('A3').value = title;
  ws.getCell('A3').font = { name: 'Calibri', size: 11, bold: true, color: { argb: INK } };

  for (const param of params) {
    const pRow = ws.addRow([]);
    ws.mergeCells(`A${pRow.number}:${lastCol}${pRow.number}`);
    ws.getCell(`A${pRow.number}`).value = param;
    ws.getCell(`A${pRow.number}`).font = { name: 'Calibri', size: 9, color: { argb: INK_SOFT } };
  }

  const genRow = ws.addRow([]);
  ws.mergeCells(`A${genRow.number}:${lastCol}${genRow.number}`);
  ws.getCell(`A${genRow.number}`).value = generatedLabel;
  ws.getCell(`A${genRow.number}`).font = { name: 'Calibri', size: 8, italic: true, color: { argb: INK_SOFT } };

  ws.addRow([]); // spacer

  // ── Header band ───────────────────────────────────────────────────────────
  const header = ws.addRow(columns.map((c) => c.header));
  header.height = 18;
  header.eachCell((cell, colIdx) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY } };
    cell.font = { name: 'Calibri', size: 9, bold: true, color: { argb: WHITE } };
    cell.alignment = { vertical: 'middle', horizontal: colAlignment(columns[colIdx - 1]!) };
  });
  ws.views = [{ state: 'frozen', ySplit: header.number }];

  // ── Data rows ─────────────────────────────────────────────────────────────
  let dataRowIndex = 0;
  for (const row of rows) {
    const values = columns.map((col) => {
      // Inline images are a PDF-only feature; never emit the data URL as text.
      if (col.image) return '';
      const raw = row.cells[col.key];
      if (col.num) {
        if (raw === null || raw === undefined || raw === '') return null;
        const n = typeof raw === 'number' ? raw : Number(raw);
        return Number.isNaN(n) ? null : n;
      }
      return raw ?? '';
    });

    const wsRow = ws.addRow(values);
    const isZebra = dataRowIndex % 2 === 1;

    wsRow.eachCell({ includeEmpty: true }, (cell, colIdx) => {
      const col = columns[colIdx - 1]!;
      cell.font = { name: 'Calibri', size: 9, color: { argb: INK_SOFT } };
      cell.border = { bottom: thin };
      cell.alignment = { horizontal: colAlignment(col) };

      if (col.num) {
        cell.numFmt = NUM_FMT;
        cell.font = { name: 'Calibri', size: 9, color: { argb: INK } };
      }

      // Emphasis overrides zebra
      if (row.emphasis === 'group') {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY_GROUP } };
        cell.font = { ...cell.font, bold: true, color: { argb: NAVY } };
      } else if (row.emphasis === 'subtotal') {
        cell.font = { ...cell.font, bold: true };
        cell.border = { ...cell.border, top: thin };
      } else if (row.emphasis === 'total') {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY_SOFT } };
        cell.font = { ...cell.font, bold: true, color: { argb: NAVY } };
      } else if (isZebra) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ZEBRA } };
      }
    });

    dataRowIndex++;
  }

  return workbook;
}

/** Browser entry point: build and trigger a download. */
export async function exportTableReportXlsx(doc: ReportTableDoc, filename: string): Promise<void> {
  const workbook = await buildTableReportWorkbook(doc);
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
